import { useState, useEffect } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';

/**
 * A hook that safely uses PostHog feature flags without causing SSR hydration mismatches.
 * Returns the default value during SSR and initial render, then updates to the correct value once
 * the component has mounted on the client side.
 *
 * @param flagKey - The PostHog feature flag key
 * @param defaultValue - The default value to use during SSR (defaults to false)
 * @param serverSideFlags - Pre-evaluated server-side flags (optional)
 * @returns The feature flag value, safe for SSR
 */
export function useSSRSafeFeatureFlag(
  flagKey: string,
  defaultValue?: boolean,
  serverSideFlags?: Record<string, boolean>,
): boolean {
  const [isClient, setIsClient] = useState(false);
  const [mounted, setMounted] = useState(false);
  const featureFlagValue = useFeatureFlagEnabled(flagKey);

  useEffect(() => {
    setIsClient(true);
    setMounted(true);
  }, []);

  // If we have server-side flags, use them during SSR
  if (!isClient && serverSideFlags && flagKey in serverSideFlags) {
    return serverSideFlags[flagKey];
  }

  // During SSR and initial render, return the default value to prevent hydration mismatch
  if (!isClient || !mounted) {
    return defaultValue ?? false;
  }

  // Once mounted on client, return the actual feature flag value
  return featureFlagValue ?? defaultValue ?? false;
}

export default useSSRSafeFeatureFlag;
